import Evaporate from 'evaporate';
import sparkMd5 from 'spark-md5';
import commonApiService from '../api/commonApiService';

let instance: UploadService;

export interface TicketData {
    ticket: string;
    mimeType: string;
    key: string;
    [key: string]: any;
}

export enum ErrorTypes {
    FILE_EXTENSION_NOT_ALLOWED = 'FILE_EXTENSION_NOT_ALLOWED',
    FILE_TOO_LARGE = 'FILE_TOO_LARGE',
    UPLOAD_ERROR = 'UPLOAD_ERROR',
}

export enum processSteps {
    INITIAL = 'INITIAL',
    FILE_SELECTION = 'FILE_SELECTION',
    FILE_UPLOADING = 'FILE_UPLOADING',
    FILE_UPLOADED = 'FILE_UPLOADED',
    FILE_NOT_ACCEPTED = 'FILE_NOT_ACCEPTED',
    ERROR = 'ERROR',
}


class UploadService {
    evaporateInstance: Evaporate;
    uploaderToken: string;
    bucket: string;
    static async getInstance(uploaderToken: string): Promise<UploadService> {
        if (instance) {
            return Promise.resolve(instance);
        }
        const config: any = await commonApiService.getUploaderInfo();
        const evaporateInstance = new Evaporate({
            aws_key: config.accessKey, // eslint-disable-line @typescript-eslint/camelcase
            aws_url: config.awsUrl, // eslint-disable-line @typescript-eslint/camelcase
            awsRegion: 'eu-west-1',
            awsSignatureVersion: '2',
            bucket: config.bucket,
            partSize: 6 * 1024 * 1024,
            maxConcurrentParts: 5,
            computeContentMd5: true,
            cryptoMd5Method(data) {
                return btoa(sparkMd5.ArrayBuffer.hash(data, true));
            },
            signerUrl: '/ICSXapi/assessment/plain/SignURL',
            timeUrl: '/ICSXapi/assessment/plain/GetServerTime',
        });
        instance = new UploadService(evaporateInstance, uploaderToken, config.bucket);
        return instance;
    }

    static setInstance(newInstance: UploadService) {
        instance = newInstance;
    }

    constructor(evaporateInstance: Evaporate, uploaderToken: string, bucket: string) {
        this.evaporateInstance = evaporateInstance;
        this.uploaderToken = uploaderToken;
        this.bucket = bucket;
    }

    uploadFile(s3FileName: string, file: File, ticket: string, callback: any): Promise<string> {
        const options: Evaporate.AddConfig = {
            file,
            name: s3FileName,
            contentType: file.type,
            notSignedHeadersAtInitiate: {
                'Cache-Control': 'max-age=3600',
            },
            xAmzHeadersAtInitiate: {
                'x-amz-acl': 'private',
            },
            started(fileKey: string) {
                callback('started', file, fileKey);
            },
            pausing(fileKey: string) {
                callback('pausing', file, fileKey);
            },
            paused(fileKey: string) {
                callback('paused', file, fileKey);
            },
            resumed(fileKey: string) {
                callback('resumed', file, fileKey);
            },
            cancelled() {
                callback('cancelled', file);
            },
            complete(xhr: XMLHttpRequest, awsObjectKey: string) {
                callback('complete', file, awsObjectKey);
            },
            progress(progress: number) {
                callback('progress', file, progress);
            },
            warn(message: string) {
                callback('warning', file, message);
            },
            error(message: string) {
                callback('error', file, message);
            },
            info(message: string) {
                callback('info', file, message);
            },
        };

        const overrideOptions: Evaporate.AddOverrideOptions = {
            signHeaders: {},
            bucket: this.bucket,
            signParams: {
                ticket,
                mimeType: file.type,
                fileSize: file.size,
            },
        };

        return this.evaporateInstance.add(options, overrideOptions);
    }

    cancelUpload(s3FileName: string) {
        const fileKey = `${this.bucket}/${s3FileName}`;

        return this.evaporateInstance.cancel(fileKey);
    }
}

export default UploadService;
